import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { handleError } from '../../../../../shared/ErrorCatching';
import { useTelemetryContext } from '../../../../../telemetry/telemetryContext';
import { getUserInfo } from '../../../../../telemetry/utils';
import { Translate } from '../../../../../shared/LanguageService';
import * as Actions from '../Actions';
import BasicOrderInfoApi from '../BasicOrderInfoApi';
import { isBookableOrder, isPhotoOrder } from '../orderTypeChecks';
import { IBasicOrderInfo, IOrderRow, IProduct, PhotoOrPlan } from '../Types';

export function getBrokerFirmAddress(firm: any): string {
    if (!firm) return '';
    return (firm.visitorAddress || '').trim();
}

export function getBrokerFirmZip(firm: any): string {
    if (!firm) return '';
    return (firm.visitorZip || '').trim();
}

export function getBrokerFirmCity(firm: any): string {
    if (!firm) return '';
    return (firm.visitorCity || '').trim();
}

export function hasKeyPickupProductSelected(
    orderRow: IOrderRow,
    possibleProducts: any[]
): boolean {
    if (!orderRow || !possibleProducts || possibleProducts.length === 0) return false;

    const product = possibleProducts.find((p) => String(p.id) === String(orderRow.productId));
    return Boolean(product ? product.hasKeyPickup : false);
}

export function validateKeyPickupAddress(orderRow: IOrderRow, possibleProducts: any[]): any {
    const errors: any = {};

    if (!hasKeyPickupProductSelected(orderRow, possibleProducts)) return errors;

    const street = String((orderRow as any).keyPickupAddress || '').trim();
    const zip = String((orderRow as any).keyPickupZip || '').trim();
    const city = String((orderRow as any).keyPickupCity || '').trim();

    if (!street)
        errors.keyPickupAddress = Translate(
            'view-order/basic-order-info/modal/validation/form/key-pickup-street'
        );
    if (!zip)
        errors.keyPickupZip = Translate(
            'view-order/basic-order-info/modal/validation/form/key-pickup-zip'
        );
    if (!city)
        errors.keyPickupCity = Translate(
            'view-order/basic-order-info/modal/validation/form/key-pickup-city'
        );

    return errors;
}

const emptyOrderRow: IOrderRow = {
    id: -1,
    productId: -1,
    productName: '',
    productPriceBrokerFirm: -1,
    productPricePropertyOwner: -1,
    photographerCompensation: -1,
    photographerCompensationSetManually: false,
    quantity: -1,
    invoiceComment: '',
    photographerComment: '',
    removed: false,
    productTypeId: -1,
    manuallySetCompensationBackup: -1,
    photoOrPlan: PhotoOrPlan.None,
    invoiceStatus: { value: -1, displayName: '' },
    canChange: false,
    canRemove: false,
    changeStatus: { value: 0, displayName: '' }
};

type UseOrderRowEditingProps = {
    orderId: number;
    order: IBasicOrderInfo;
    user: any;
    showSuccess: (text: string) => void;
    showWarning: (text: string) => void;
};

export function useOrderRowEditing(props: UseOrderRowEditingProps) {
    const { orderId, order, user, showSuccess, showWarning } = props;
    const dispatch = useDispatch<any>();
    const { logger } = useTelemetryContext();

    const [selectedProduct, setSelectedProduct] = useState(-1);
    const [orderRow, setOrderRow] = useState<IOrderRow>({ ...emptyOrderRow });
    const [formErrors, setFormErrors] = useState<any>({});
    const [doing, setDoing] = useState(false);
    const [showAddOrderRowModal, setShowAddOrderRowModal] = useState(false);
    const [showEditOrderRowModal, setShowEditOrderRowModal] = useState(false);
    const [showEditOrderRowConfirmModal, setShowEditOrderRowConfirmModal] = useState(false);
    const [includeEditOrderRowFileRemovalWarning, setIncludeEditOrderRowFileRemovalWarning] =
        useState(false);
    const [possibleProductsForUpdate, setPossibleProductsForUpdate] = useState<any[]>([]);

    const productSelected = (productId: number): void => {
        let product = order.customer.possibleProducts.filter((p: IProduct) => p.id === productId)[0];

        if (product == null) {
            product = order.possibleProductsForUpdate.filter(
                (p: IProduct) => p.id === productId
            )[0];
        }

        const nextRow: IOrderRow = orderRow ? { ...orderRow } : ({} as IOrderRow);

        nextRow.productName = product.name;
        nextRow.productPriceBrokerFirm = product.priceBrokerFirm;
        nextRow.productId = product.id;
        nextRow.photographerCompensation = nextRow.photographerCompensationSetManually
            ? nextRow.photographerCompensation
            : isBookableOrder(order) && product.photoOrPlan === PhotoOrPlan.Photo
            ? product.photographerCompensation
            : null;
        nextRow.productPricePropertyOwner = product.pricePropertyOwner;

        const hasKeyPickup = Boolean(product && product.hasKeyPickup);
        const customer = order && order.customer ? order.customer : null;

        (nextRow as any).useBrokerFirmAddress = hasKeyPickup;

        if (hasKeyPickup) {
            (nextRow as any).keyPickupAddress = getBrokerFirmAddress(customer);
            (nextRow as any).keyPickupZip = getBrokerFirmZip(customer);
            (nextRow as any).keyPickupCity = getBrokerFirmCity(customer);
        } else {
            (nextRow as any).keyPickupAddress = '';
            (nextRow as any).keyPickupZip = '';
            (nextRow as any).keyPickupCity = '';
        }

        setOrderRow(nextRow);
    };

    const onChange = (event: any): void => {
        if (event.target.name === 'product') {
            const value = event.target.value === '' ? -1 : Number(event.target.value);
            setSelectedProduct(value);
            productSelected(value);
        } else {
            setOrderRow({
                ...orderRow,
                [event.target.name]:
                    event.target.type === 'checkbox' ? event.target.checked : event.target.value
            });
        }
    };

    const onChangeKeyPickupCheckbox = (event: any): void => {
        const { name, checked } = event.target;

        onChange(event);

        if (name !== 'useBrokerFirmAddress' || !checked) return;

        const customer = order && order.customer ? order.customer : null;

        const address = getBrokerFirmAddress(customer);
        const zip = getBrokerFirmZip(customer);
        const city = getBrokerFirmCity(customer);

        setOrderRow((previousRow) => ({
            ...previousRow,
            keyPickupAddress: address,
            keyPickupZip: zip,
            keyPickupCity: city
        }));
        setFormErrors((previousErrors: any) => {
            const nextErrors = { ...(previousErrors || {}) };
            delete nextErrors[name];
            return nextErrors;
        });
    };

    const onPhotographerCompensationSetManuallyChange = (event: any): void => {
        onChange(event);

        if (!orderRow.photographerCompensationSetManually) {
            const product = order.customer.possibleProducts.filter(
                (x: any) => x.id === orderRow.productId
            )[0];
            if (!product) {
                return;
            }
            const automaticCompensation = orderRow.photographerCompensation;
            setOrderRow((previousRow) => ({
                ...previousRow,
                manuallySetCompensationBackup: automaticCompensation,
                photographerCompensation: product.photographerCompensation
            }));
        } else if (orderRow.manuallySetCompensationBackup != null) {
            setOrderRow((previousRow) => ({
                ...previousRow,
                photographerCompensation: previousRow.manuallySetCompensationBackup
            }));
        }
    };

    const toggleAddOrderRowModal = (): void => {
        const shouldCloseModal = showAddOrderRowModal;
        let placeholder: IOrderRow;

        if (shouldCloseModal) {
            placeholder = null;
        } else {
            const product = order.customer.possibleProducts.filter(
                (p: IProduct) => p.id === selectedProduct
            )[0];

            const hasKeyPickup = Boolean(product && product.hasKeyPickup);
            const customer = order && order.customer ? order.customer : null;

            placeholder = {
                ...emptyOrderRow,
                productId: product.id,
                productName: product.name,
                productPriceBrokerFirm: product.priceBrokerFirm,
                productPricePropertyOwner: product.pricePropertyOwner,
                photographerCompensation:
                    isPhotoOrder(order) && product.photoOrPlan === PhotoOrPlan.Photo
                        ? product.photographerCompensation
                        : null,
                quantity: 1,
                invoiceComment: null,
                manuallySetCompensationBackup: product.photographerCompensation,
                photoOrPlan: product.photoOrPlan,
                invoiceStatus: { value: 0, displayName: '' },
                useBrokerFirmAddress: hasKeyPickup,
                keyPickupAddress: hasKeyPickup ? getBrokerFirmAddress(customer) : '',
                keyPickupZip: hasKeyPickup ? getBrokerFirmZip(customer) : '',
                keyPickupCity: hasKeyPickup ? getBrokerFirmCity(customer) : ''
            };
        }

        setShowAddOrderRowModal(!shouldCloseModal);
        setOrderRow(placeholder);
        setFormErrors({});
        setDoing(false);
    };

    const addOrderRow = (): void => {
        const clientErrors = validateKeyPickupAddress(orderRow, order.customer.possibleProducts);

        if (Object.keys(clientErrors).length > 0) {
            setFormErrors(clientErrors);
            return;
        }
        setDoing(true);

        const selectedProductForOrderRow = order.customer.possibleProducts.find(
            (p: IProduct) => p.id === orderRow.productId
        );
        const productPhotographerCompensation = selectedProductForOrderRow
            ? selectedProductForOrderRow.photographerCompensation
            : null;

        const data: any = { ...orderRow };
        delete data.id;

        if (isPhotoOrder(order)) {
            data.productPhotographerCompensationSetManually =
                orderRow.photographerCompensation !== productPhotographerCompensation;
        }

        dispatch(Actions.createOrderRow(orderId, data))
            .then((result: any) => {
                setShowAddOrderRowModal(false);
                setOrderRow({
                    ...emptyOrderRow,
                    photographerCompensation: isBookableOrder(order) ? -1 : null,
                    manuallySetCompensationBackup: productPhotographerCompensation
                });
                setPossibleProductsForUpdate([]);
                setFormErrors({});
                setDoing(false);
                setSelectedProduct(-1);

                if (result.canPhotographerHandleProduct === false) {
                    showWarning(
                        Translate(
                            'view-order/basic-order-info/products/notifications/photographer-cant-handle-product'
                        )
                    );
                }

                showSuccess(
                    Translate('view-order/basic-order-info/products/notifications/added')
                );
            })
            .catch((error: any) =>
                handleError(error, (errors: any): void => {
                    setFormErrors(errors);
                    setDoing(false);
                })
            );
    };

    const editOrderRow = (rowId: number): void => {
        dispatch(Actions.editOrderRow(orderId, rowId))
            .then((viewModel: any) => {
                const rowToEdit =
                    (viewModel && viewModel.currentOrderRow) ||
                    order.orderRows.find((r: IOrderRow) => r.id === rowId);

                const totalKeyPickupAddress =
                    viewModel && viewModel.totalKeyPickupAddress
                        ? viewModel.totalKeyPickupAddress
                        : null;

                const keyPickupAddress = String(
                    totalKeyPickupAddress && totalKeyPickupAddress.keyPickupAddress
                        ? totalKeyPickupAddress.keyPickupAddress
                        : ''
                ).trim();
                const keyPickupZip =
                    totalKeyPickupAddress && totalKeyPickupAddress.keyPickupZip != null
                        ? totalKeyPickupAddress.keyPickupZip
                        : undefined;
                const keyPickupCity = String(
                    totalKeyPickupAddress && totalKeyPickupAddress.keyPickupCity
                        ? totalKeyPickupAddress.keyPickupCity
                        : ''
                ).trim();

                const customer = order ? order.customer : null;

                const autoAddress = String(getBrokerFirmAddress(customer) || '').trim();
                const autoZip = String(getBrokerFirmZip(customer) || '').trim();
                const autoCity = String(getBrokerFirmCity(customer) || '').trim();

                const zipAsString = String(keyPickupZip != null ? keyPickupZip : '').trim();

                setShowEditOrderRowModal(true);
                setOrderRow({
                    ...rowToEdit,
                    keyPickupAddress,
                    keyPickupZip,
                    keyPickupCity,
                    useBrokerFirmAddress:
                        !!keyPickupAddress &&
                        !!autoAddress &&
                        keyPickupAddress === autoAddress &&
                        zipAsString === autoZip &&
                        keyPickupCity === autoCity
                });
                setPossibleProductsForUpdate(
                    (viewModel && viewModel.possibleProductsForUpdate) ||
                        order.possibleProductsForUpdate
                );
                setFormErrors({});
                setDoing(false);
            })
            .catch((error: any) => handleError(error));
    };

    const closeEditOrderRowModal = (): void => {
        setShowEditOrderRowModal(false);
        setOrderRow({ ...emptyOrderRow });
        setFormErrors({});
        setDoing(false);
    };

    const updateOrderRow = (): void => {
        setDoing(true);
        dispatch(Actions.updateOrderRow(orderId, orderRow))
            .then((result: any) => {
                setShowEditOrderRowModal(false);
                dispatch(Actions.updateImageEditorFirmSuccess(result.imageEditorFirmId));
                setOrderRow({
                    ...emptyOrderRow,
                    photographerCompensation: isBookableOrder(order) ? -1 : null
                });
                setFormErrors({});
                setDoing(false);

                if (result.canPhotographerHandleProduct === false) {
                    showWarning(
                        Translate(
                            'view-order/basic-order-info/products/notifications/photographer-cant-handle-product'
                        )
                    );
                }

                showSuccess(
                    Translate('view-order/basic-order-info/products/notifications/updated')
                );
            })
            .catch((error: any) =>
                handleError(error, (errors: any): void => {
                    setFormErrors(errors);
                    setDoing(false);
                })
            );
    };

    const toggleEditRowConfirmation = (): void => {
        const newStatus = !showEditOrderRowConfirmModal;

        if (newStatus === true) {
            const clientErrors = validateKeyPickupAddress(orderRow, possibleProductsForUpdate);

            if (Object.keys(clientErrors).length > 0) {
                setFormErrors(clientErrors);
                return;
            }
            BasicOrderInfoApi.getOrderRowEditStatus(orderId, orderRow.id, orderRow.productId)
                .then((response) => {
                    setShowEditOrderRowConfirmModal(newStatus);
                    setIncludeEditOrderRowFileRemovalWarning(
                        response.operationWillCauseFilesToBeRemoved
                    );
                })
                .catch((error: any) => {
                    logger.logException(error, {
                        origin: 'BasicOrderInfoPage.OrderRowEditing.getOrderRowEditStatus',
                        userInfo: getUserInfo(user)
                    });
                });
        } else {
            setShowEditOrderRowConfirmModal(newStatus);
            setIncludeEditOrderRowFileRemovalWarning(false);
        }
    };

    const removeOrderRow = (orderRowToRemove: IOrderRow): void => {
        dispatch(Actions.removeOrderRow(orderId, orderRowToRemove))
            .then(() => {
                showSuccess(
                    Translate('view-order/basic-order-info/products/notifications/removed')
                );
            })
            .catch((error: any) => handleError(error));
    };

    return {
        selectedProduct,
        orderRow,
        formErrors,
        doing,
        showAddOrderRowModal,
        showEditOrderRowModal,
        showEditOrderRowConfirmModal,
        includeEditOrderRowFileRemovalWarning,
        possibleProductsForUpdate,
        onChange,
        onChangeKeyPickupCheckbox,
        onPhotographerCompensationSetManuallyChange,
        toggleAddOrderRowModal,
        addOrderRow,
        editOrderRow,
        closeEditOrderRowModal,
        updateOrderRow,
        toggleEditRowConfirmation,
        removeOrderRow
    };
}
